import React from "react";
import PageMargins from "~/components/styled/PageMargins";
import { Heading } from "~/components/ui/Heading";
import { Table } from "~/components/ui/Table";
import { IconLink } from "~/components/ui/IconLink";
import { humanFileSize } from "~/utils/humanFileSize";
import safeHtml from "~/utils/sanitize";
import styled from "styled-components";

const Square = styled.div`
  margin: auto;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        width: ${props.theme.spacePx(
          breakpoint,
          ["base", "mobile"].includes(breakpoint) ? 7 : 8
        )};          
        height: ${props.theme.spacePx(
          breakpoint,
          ["base", "mobile"].includes(breakpoint) ? 7 : 8
        )};          
    `;
    })}
`;

export const Documents = ({
  documents,
  heading = "Downloads",
  tableOnly,
}: {
  documents: any;
  heading?: string;
  tableOnly?: boolean;
}) => {
  if (!documents?.length) return <></>;

  const filtered = documents?.filter(
    (d: any) => d?.file_title && d?.attachment?.url
  );

  if (!filtered?.length) return <></>;

  const table = (
    <Table
      headers={[
        {
          label: "Document Name",
          breakpointWidth: {
            base: "auto",
            mobile: "auto",
            mobileLandscape: "auto",
            tablet: "auto",
            desktop: "auto",
            tabletLandscape: "auto",
            screen: "auto",
          },
        },
        {
          label: "Type",
          breakpointWidth: {
            base: "60px",
            mobile: "60px",
            mobileLandscape: "60px",
            tablet: "100px",
            tabletLandscape: "150px",
            desktop: "150px",
            screen: "200px",
          },
        },
        {
          label: "Size",
          visibleAt: "tabletLandscape",
          breakpointWidth: {
            tabletLandscape: "150px",
            desktop: "200px",
            screen: "250px",
          },
        },
        {
          label: "Download",
          textAlign: "right",
          breakpointWidth: {
            base: "85px",
            mobile: "85px",
            mobileLandscape: "85px",
            tablet: "105px",
            tabletLandscape: "120px",
            desktop: "120px",
            screen: "148px",
          },
        },
      ]}
      rows={filtered?.map((document: any, index: number) => {
        return {
          url: document?.attachment?.url,
          targetBlank: true,
          cells: [
            {
              verticalAlign: "center",
              content: (
                <div style={{ width: "100%" }}>
                  <Heading
                    key={`${index}-3`}
                    space={6}
                    heading="h3"
                    html={document?.file_title ?? ""}
                    fontWeight="400"
                    inline
                    noWrap
                    trimmed
                    textTransform="none"
                    spaceTop={9}
                  />
                </div>
              ),
            },
            {
              verticalAlign: "center",
              content: (
                <Heading
                  key={`${index}-2`}
                  space={7}
                  heading="h3"
                  inline
                  noWrap
                  trimmed
                  fontWeight="400"
                  spaceTop={9}
                >
                  {document?.attachment?.url?.split(".").pop() ?? "OTHER"}
                </Heading>
              ),
            },
            {
              visibleAt: "tabletLandscape",
              verticalAlign: "center",
              content: (
                <Heading
                  key={`${index}-2`}
                  space={7}
                  heading="h3"
                  inline
                  noWrap
                  trimmed
                  fontWeight="400"
                  spaceTop={9}
                >
                  {document?.attachment?.filesize
                    ? humanFileSize(document?.attachment?.filesize)
                    : "UNKNOWN"}
                </Heading>
              ),
            },

            {
              textAlign: "right",
              verticalAlign: "center",
              content: document?.attachment?.url ? (
                <Square>
                  <IconLink
                    key={`${index}-3`}
                    type="download"
                    url={document?.attachment?.url}
                    target="_blank"
                    rel="noreferral"
                    title={`Download ${safeHtml(document?.file_title)}`}
                  />
                </Square>
              ) : undefined,
            },
          ],
        };
      })}
    />
  );

  if (tableOnly) return table;

  return (
    <PageMargins>
      <Heading heading="h3" asTag space={8} html={heading} />
      {table}
    </PageMargins>
  );
};

export default Documents;
